/**
 * ServerDepotUS - Live chat gate
 *
 * Shopify Inbox offers no pre-chat form, so the native <shopify-chat> panel stays
 * locked until the visitor leaves first name, last name, email and phone. The details
 * are mailed to sales through the same Worker the quote form uses, so a conversation
 * that is abandoned after one message still leaves a lead behind.
 *
 * The <html class="sd-chat-locked"> flag is set by an inline script in theme.liquid so
 * the native bubble never flashes before this file loads.
 */
(function () {
  var LEAD_KEY = 'sd_chat_lead';
  var PENDING_KEY = 'sd_chat_lead_pending';
  var ENDPOINT = 'https://api.serverdepotus.com/quote';
  var SEND_TIMEOUT = 8000;

  var root = document.documentElement;
  var overlay = null;
  var launcher = null;

  function readJson(key) {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJson(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }
  function drop(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  function send(lead) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, SEND_TIMEOUT) : null;
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lead),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      if (!res.ok) throw new Error('lead endpoint returned ' + res.status);
      return true;
    }, function (err) {
      if (timer) clearTimeout(timer);
      throw err;
    });
  }

  /* A lead that could not be delivered is kept and retried on the next page view -
     the whole point of the gate is that the contact details are not lost. */
  function flushPending() {
    var pending = readJson(PENDING_KEY);
    if (!pending) return;
    send(pending).then(function () { drop(PENDING_KEY); }, function () { /* try again later */ });
  }

  function openNativeChat() {
    var tries = 0;
    (function attempt() {
      var el = document.querySelector('shopify-chat');
      if (el) {
        if (typeof el.show === 'function') {
          try { el.show(); return; } catch (e) { /* fall through to the activator */ }
        }
        if (el.shadowRoot) {
          var button = el.shadowRoot.querySelector('.activator') || el.shadowRoot.querySelector('button');
          if (button) { button.click(); return; }
        }
      }
      if (++tries < 40) setTimeout(attempt, 250);
    })();
  }

  function unlock() {
    root.classList.remove('sd-chat-locked');
    closeGate();
    openNativeChat();
  }

  function closeGate() {
    if (!overlay) return;
    overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  function openGate() {
    if (!overlay) return;
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    var first = overlay.querySelector('#sd-chatgate-first');
    if (first) setTimeout(function () { first.focus(); }, 50);
  }

  function digits(value) {
    return value.replace(/[^0-9]/g, '');
  }

  function validate(fields) {
    if (fields.first.value.trim().length < 2) return { field: fields.first, message: 'Please enter your first name.' };
    if (fields.last.value.trim().length < 2) return { field: fields.last, message: 'Please enter your last name.' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(fields.email.value.trim())) {
      return { field: fields.email, message: 'Please enter a valid email address.' };
    }
    var phone = digits(fields.phone.value);
    if (phone.length < 10 || phone.length > 15) {
      return { field: fields.phone, message: 'Please enter a valid phone number (at least 10 digits).' };
    }
    return null;
  }

  function build() {
    launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'sd-chatgate-launcher';
    launcher.setAttribute('aria-haspopup', 'dialog');
    launcher.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>' +
      '</svg><span>Chat with us</span>';
    document.body.appendChild(launcher);
    // Tells the safety net in theme.liquid that the gate is up and the lock can stand.
    window.__sdChatGateReady = true;

    overlay = document.createElement('div');
    overlay.className = 'sd-chatgate-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Start a live chat');
    overlay.innerHTML =
      '<div class="sd-chatgate-modal">' +
        '<button type="button" class="sd-chatgate-close" aria-label="Close">&times;</button>' +
        '<h2>Before we chat</h2>' +
        '<p class="sd-chatgate-intro">Tell us how to reach you and our team will pick up the conversation right away.</p>' +
        '<form id="sd-chatgate-form" novalidate>' +
          '<div class="sd-chatgate-row">' +
            '<div class="sd-chatgate-field">' +
              '<label for="sd-chatgate-first">First Name <span>*</span></label>' +
              '<input type="text" id="sd-chatgate-first" name="first_name" autocomplete="given-name" required>' +
            '</div>' +
            '<div class="sd-chatgate-field">' +
              '<label for="sd-chatgate-last">Last Name <span>*</span></label>' +
              '<input type="text" id="sd-chatgate-last" name="last_name" autocomplete="family-name" required>' +
            '</div>' +
          '</div>' +
          '<div class="sd-chatgate-field">' +
            '<label for="sd-chatgate-email">Email <span>*</span></label>' +
            '<input type="email" id="sd-chatgate-email" name="email" autocomplete="email" placeholder="you@company.com" required>' +
          '</div>' +
          '<div class="sd-chatgate-field">' +
            '<label for="sd-chatgate-phone">Phone <span>*</span></label>' +
            '<input type="tel" id="sd-chatgate-phone" name="phone" autocomplete="tel" placeholder="(555) 123-4567" required>' +
          '</div>' +
          '<button type="submit" class="sd-chatgate-submit">Start Chat</button>' +
          '<div class="sd-chatgate-error"></div>' +
          '<p class="sd-chatgate-note">We use these details only to answer your question. All fields are required.</p>' +
        '</form>' +
      '</div>';
    document.body.appendChild(overlay);

    var form = overlay.querySelector('#sd-chatgate-form');
    var errorBox = overlay.querySelector('.sd-chatgate-error');
    var submit = overlay.querySelector('.sd-chatgate-submit');
    var fields = {
      first: overlay.querySelector('#sd-chatgate-first'),
      last: overlay.querySelector('#sd-chatgate-last'),
      email: overlay.querySelector('#sd-chatgate-email'),
      phone: overlay.querySelector('#sd-chatgate-phone')
    };

    function showError(problem) {
      errorBox.textContent = problem.message;
      errorBox.classList.add('active');
      Object.keys(fields).forEach(function (key) { fields[key].classList.remove('invalid'); });
      problem.field.classList.add('invalid');
      problem.field.focus();
    }

    launcher.addEventListener('click', openGate);
    overlay.querySelector('.sd-chatgate-close').addEventListener('click', closeGate);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeGate();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeGate();
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var problem = validate(fields);
      if (problem) { showError(problem); return; }

      errorBox.classList.remove('active');
      Object.keys(fields).forEach(function (key) { fields[key].classList.remove('invalid'); });

      var first = fields.first.value.trim();
      var last = fields.last.value.trim();
      var lead = {
        type: 'chat_lead',
        name: first + ' ' + last,
        first_name: first,
        last_name: last,
        email: fields.email.value.trim(),
        phone: fields.phone.value.trim(),
        product: 'Live Chat',
        page_url: window.location.href,
        page_title: document.title
      };

      submit.disabled = true;
      submit.textContent = 'Opening chat...';
      if (window.sdTrackLead) window.sdTrackLead();

      /* The visitor is let through either way - a delivery problem on our side is not
         a reason to block them - but an undelivered lead is queued for the next visit. */
      send(lead).then(function () {
        writeJson(LEAD_KEY, lead);
        unlock();
      }, function () {
        writeJson(LEAD_KEY, lead);
        writeJson(PENDING_KEY, lead);
        unlock();
      });
    });
  }

  function start() {
    flushPending();
    if (!root.classList.contains('sd-chat-locked')) return;
    if (readJson(LEAD_KEY)) { root.classList.remove('sd-chat-locked'); return; }
    build();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
